import { Box } from '@mui/material';
import { DataGridPro, DataGridProProps, GridFeatureMode, GridRowModel, GridSortModel } from '@mui/x-data-grid-pro';
import React, { useEffect, useState, useCallback, useRef } from 'react';
import { Observable, Subscription } from 'rxjs';

import { GeneralConstants } from 'app/constants/general-constants';
import { TimingConstants } from 'app/constants/timing-constants';
import { KnpPaginationData } from 'app/model/knp-pagination-data';
import { ListOptions } from 'app/model/list-options';
import { RestfulModel } from 'app/model/restful-model';
import { RestfulApiService } from 'app/services/restful-api.service';

type OverriddenGridOptions = Pick<
    DataGridProProps,
    | 'onPaginationModelChange'
    | 'onSortModelChange'
    | 'pageSizeOptions'
    | 'pagination'
    | 'paginationMode'
    | 'paginationModel'
    | 'rowCount'
    | 'rows'
    | 'sortingMode'
    | 'sortModel'
>;

interface PaginatedDataGridProps<T> extends Omit<DataGridProProps, keyof OverriddenGridOptions> {
    service?: RestfulApiService<RestfulModel, unknown>;
    searchProps?: T;
    onSearch?: (options: ListOptions, searchProps: T) => Observable<KnpPaginationData<GridRowModel>>;
    dataSide: GridFeatureMode;
    listOptions?: ListOptions;
    restParent?: unknown;
    fieldAlias?: string;
}

export const PaginatedDataGrid = <P,>(props: PaginatedDataGridProps<P>): React.ReactElement => {
    if (!props.service && !props.onSearch) {
        throw new Error('Either "service" or "onSearch" must be specified');
    }

    if ('client' === props.dataSide && !props.service) {
        throw new Error('For client side handling the "service" must be defined');
    }

    const [rows, setRows] = useState<GridRowModel[]>([]);
    const [rowCount, setRowCount] = useState(0);

    const [page, setPage] = useState(0);
    const [pageSize, setPageSize] = useState(GeneralConstants.PAGE_SIZE_NORMAL);

    const [sortModel, setSortModel] = useState<GridSortModel>(props.initialState?.sorting?.sortModel ?? []);

    const { service, searchProps, onSearch, dataSide, listOptions, restParent, fieldAlias, ...rest } = props;

    const sortAlias = fieldAlias ? fieldAlias : 'spec';

    const debounceRef = useRef<NodeJS.Timeout | null>(GeneralConstants.COMPONENT_VALUE_NULL);

    const doList = useCallback(
        (updatedOpts?: ListOptions, updatedSort?: GridSortModel): Subscription | undefined => {
            const sort: Record<string, string> = {};
            for (const sortItem of updatedSort || sortModel) {
                if (!sortItem.sort) {
                    continue;
                }
                sort[`${sortAlias}.${sortItem.field}`] = sortItem.sort;
            }
            const opts: ListOptions = {
                ...listOptions,
                page: page + 1, // rest is 1 based grid is 0 based
                pageSize,
                sort,
                ...updatedOpts,
            };

            if ('client' === dataSide) {
                // all has been preloaded to client, no more server requests
                return undefined;
            }

            if (onSearch) {
                return onSearch(opts, searchProps || ({} as P)).subscribe((pagination) => {
                    setPage(pagination.currentPage - 1); // rest is 1 based grid is 0 based
                    setRows(pagination.data);
                    setRowCount(pagination.totalItems);
                });
            }

            if (service) {
                return service.list(opts, restParent).subscribe((pagination) => {
                    setPage(pagination.currentPage - 1); // rest is 1 based grid is 0 based
                    setRows(pagination.data);
                    setRowCount(pagination.totalItems);
                });
            }

            return undefined;
        },
        [sortModel, page, pageSize, listOptions, dataSide, onSearch, searchProps, service, restParent, sortAlias],
    );

    useEffect(() => {
        let subscription: Subscription | undefined;

        if ('client' === dataSide) {
            if (service) {
                subscription = service.listAll(listOptions, restParent).subscribe((data) => {
                    setRows(data);
                    setRowCount(data.length);
                });
            }
        } else {
            if (debounceRef.current) {
                clearTimeout(debounceRef.current);
            }

            debounceRef.current = setTimeout(() => {
                doList();
            }, TimingConstants.TYPING_DELAY);
        }

        return () => {
            subscription?.unsubscribe();
            if (debounceRef.current) {
                clearTimeout(debounceRef.current);
            }
        };
    }, [dataSide, service, listOptions, restParent, searchProps, doList]);

    const overridden: OverriddenGridOptions = {
        rows,
        rowCount,
        paginationModel: {
            page,
            pageSize,
        },
        pagination: true,
        paginationMode: dataSide,
        pageSizeOptions: GeneralConstants.PAGE_SIZE_OPTIONS,
        onPaginationModelChange: ({ page: newPage, pageSize: newPageSize }) => {
            setPage(newPage);
            setPageSize(newPageSize);
            doList({
                page: newPage + 1, // rest is 1 based grid is 0 based
                pageSize: newPageSize,
            });
        },
        sortingMode: dataSide,
        sortModel,
        onSortModelChange: (newSortModel) => {
            setSortModel(newSortModel);
            doList({}, newSortModel);
        },
    };

    return (
        <Box sx={{ width: '100%', display: 'grid', height: '100%' }}>
            <DataGridPro {...rest} {...overridden} />
        </Box>
    );
};
